import React, { useCallback, useEffect, useMemo } from 'react';
import { useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import ActionButton from '@applied/dragonfruit/src/components/ActionButton/ActionButton';
import Card from '@applied/dragonfruit/src/components/Card/Card';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import { useSnackbar } from '@applied/dragonfruit/src/components/PageSnackbar/snackbar_provider';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import { useQuery } from '@tanstack/react-query';
import { getRegistrationStatus, postRegisterResetPassword } from 'api/password_reset_requests';
import { CandidateExperienceQueryKeys } from 'api/query_keys';

import { RevealablePassword } from 'components/common/RevealablePassword/RevealablePassword';

import './password-reset-page.scss';

import {
  PASSWORD_REQUIREMENTS,
  PasswordResetPageVariant,
  passwordSatisfiesRequirements,
} from '../../api/password_reset_classes';

/**
 * The props for the PasswordRequirementEntry component.
 *
 * @param description The description of the requirement.
 * @param satisfied Whether the requirement is satisfied.
 */
type PasswordRequirementEntryProps = {
  description: string;
  satisfied: boolean;
};

/**
 * The component which displays a requirement for the password
 * and if it is currently satisfied.
 */
function PasswordRequirementEntry(props: PasswordRequirementEntryProps): JSX.Element {
  const { description, satisfied } = props;
  return (
    <li className="requirement-entry">
      <Flex direction="row" justify="space-between">
        <Typography variant="body">{description}</Typography>
        <Typography className="requirement-status" variant="body">
          {satisfied ? '✓' : '✗'}
        </Typography>
      </Flex>
    </li>
  );
}

/**
 * The props for the PasswordResetPage component.
 *
 * @param variant The variant of the password reset page.
 * @param currentPassword The current password of the user.
 * @param setCurrentPassword The function to set the current password of the user.
 * @param password The new password of the user.
 * @param setPassword The function to set the new password of the user.
 * @param confirmPassword The confirmed password of the user.
 * @param setConfirmPassword The function to set the confirmed password of the user.
 */
type PasswordInputFieldsProps = {
  variant: PasswordResetPageVariant;
  currentPassword: string;
  setCurrentPassword: (currentPassword: string) => void;
  password: string;
  setPassword: (password: string) => void;
  confirmPassword: string;
  setConfirmPassword: (confirmPassword: string) => void;
  onSubmit: () => void;
};

/**
 * The components which take in user password information.
 *
 * If the user is resetting their password, they must also enter their current password.
 */
function PasswordInputFields(props: PasswordInputFieldsProps): JSX.Element {
  const {
    variant,
    currentPassword,
    setCurrentPassword,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    onSubmit,
  } = props;

  const currentPasswordField = useMemo(
    () => (
      <RevealablePassword
        switchLabel="Reveal Current Password"
        className="password-input"
        placeholder="Enter your current password..."
        caption="Current Password"
        value={currentPassword}
        onChange={setCurrentPassword}
        onSubmit={onSubmit}
      />
    ),
    [currentPassword, setCurrentPassword, onSubmit],
  );

  const passwordField = useMemo(
    () => (
      <RevealablePassword
        switchLabel="Reveal New Password"
        className="password-input"
        placeholder="Enter a new password..."
        caption="New Password"
        value={password}
        onChange={setPassword}
        onSubmit={onSubmit}
      />
    ),
    [password, setPassword, onSubmit],
  );

  const confirmPasswordField = useMemo(
    () => (
      <RevealablePassword
        switchLabel="Reveal Confirm Password"
        className="password-input"
        placeholder="Confirm your password..."
        caption="Confirm Password"
        value={confirmPassword}
        onChange={setConfirmPassword}
        onSubmit={onSubmit}
      />
    ),
    [confirmPassword, setConfirmPassword, onSubmit],
  );

  return (
    <>
      {variant === PasswordResetPageVariant.AUTH && currentPasswordField}
      {passwordField}
      {confirmPasswordField}
    </>
  );
}

/**
 * The props for the PasswordRequirementsList component.
 *
 * @param variant The variant of the password reset page.
 * @param currentPassword The current password of the user.
 * @param password The new password of the user.
 * @param confirmPassword The confirmed password of the user.
 */
type PasswordRequirementsListProps = {
  variant: PasswordResetPageVariant;
  currentPassword: string;
  password: string;
  confirmPassword: string;
};

function PasswordRequirementsList(props: PasswordRequirementsListProps): JSX.Element {
  const { variant, currentPassword, password, confirmPassword } = props;

  const requirementEntries = useMemo(
    () =>
      Object.entries(PASSWORD_REQUIREMENTS).map(([description, condition], index) => (
        <PasswordRequirementEntry
          description={description}
          satisfied={condition(password)}
          key={`requirement-${index}`}
        />
      )),
    [password],
  );

  const matchedPasswordRequirement = useMemo(() => {
    const passwordMatchesConfirmPassword = password === confirmPassword;
    return (
      <PasswordRequirementEntry
        description="Match your confirmed password"
        satisfied={passwordMatchesConfirmPassword}
      />
    );
  }, [password, confirmPassword]);

  // TODO: Compare the current password with the user's current password
  const matchedCurrentPasswordRequirement = useMemo(() => {
    const currentPasswordMatches = !!currentPassword;
    return (
      <PasswordRequirementEntry
        description="Be authorized with your current password"
        satisfied={currentPasswordMatches}
      />
    );
  }, [currentPassword]);

  return (
    <div className="requirements-container">
      <Typography className="requirements-header" variant="body">
        Your password must:
      </Typography>
      <ul className="requirements-list">
        {requirementEntries} {matchedPasswordRequirement}{' '}
        {variant === PasswordResetPageVariant.AUTH && matchedCurrentPasswordRequirement}
      </ul>
    </div>
  );
}

/**
 * The props for the SubmitPasswordResetButton component.
 *
 * @param variant The variant of the password reset page.
 * @param currentPassword The current password of the user.
 * @param password The new password of the user.
 * @param confirmPassword The confirmed password of the user.
 * @param registrationToken The registration token of the user.
 * TODO: @param authenticationTokens The authentication tokens of the user.
 */
type SubmitPasswordResetButtonProps = {
  variant: PasswordResetPageVariant;
  currentPassword: string;
  password: string;
  confirmPassword: string;
  onSubmit: () => void;
};

/**
 * The component which provides the password submission button.
 */
function SubmitPasswordResetButton(props: SubmitPasswordResetButtonProps): JSX.Element {
  const { variant, currentPassword, password, confirmPassword, onSubmit } = props;

  const validPassword = useMemo(() => {
    const passwordMatchesConfirmPassword = password === confirmPassword;
    const currentPasswordMatches = !!currentPassword;
    return (
      passwordSatisfiesRequirements(password) &&
      passwordMatchesConfirmPassword &&
      (variant === PasswordResetPageVariant.AUTH ? currentPasswordMatches : true)
    );
  }, [password, confirmPassword, currentPassword, variant]);

  return (
    <ActionButton
      className="submit-password-reset-button"
      onClick={onSubmit}
      disabled={!validPassword}
      appearance="primary"
    >
      Reset Password
    </ActionButton>
  );
}

/**
 * The props for the PasswordResetPage component.
 *
 * @param variant The variant of the password reset page.
 */
type PasswordResetPageProps = {
  variant: PasswordResetPageVariant;
  registrationToken: string;
};

/**
 * The page which allows the user to reset their password.
 *
 * If the variant is REGISTER, the user is setting the password for the first time. The change
 * is authorized using the registration token (from the URL).
 * If the variant is AUTH, the user is resetting the password. The change must be confirmed on
 * the frontend with their current password and is authorized using auth tokens (from cookies).
 */
export function PasswordResetPage(props: PasswordResetPageProps): JSX.Element {
  const { variant, registrationToken } = props;
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const history = useHistory();
  const snackbar = useSnackbar();

  // ======== Render ======== \\

  const onSubmit = useCallback(async () => {
    if (variant === PasswordResetPageVariant.REGISTER) {
      try {
        await postRegisterResetPassword(password, registrationToken);
        history.push('/login');
      } catch {
        snackbar.create('Failed to reset password');
      }
    } else {
      // TODO: use postResetPassword with authenticationTokens(from client context which pulls from cookies)
    }
  }, [variant, password, registrationToken, history, snackbar]);

  useEffect(
    function updateTabTitle() {
      document.title =
        variant === PasswordResetPageVariant.REGISTER
          ? 'Applied Intuition - Register'
          : 'Applied Intuition - Password Reset';
    },
    [variant],
  );

  return (
    <Flex className="password-reset-page" direction="column" justify="center" alignment="center">
      <Card className="password-reset-card" direction="column" gap="md" padding="xl">
        <Typography
          className="password-reset-header"
          variant="title"
          data-testid="password-reset-header"
        >
          Reset Password
        </Typography>
        <PasswordInputFields
          variant={variant}
          currentPassword={currentPassword}
          setCurrentPassword={setCurrentPassword}
          password={password}
          setPassword={setPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          onSubmit={onSubmit}
        />
        <PasswordRequirementsList
          variant={variant}
          currentPassword={currentPassword}
          password={password}
          confirmPassword={confirmPassword}
        />
        <div
          className="submit-password-reset-container"
          data-testid="submit-password-reset-container"
        >
          <SubmitPasswordResetButton
            variant={variant}
            currentPassword={currentPassword}
            password={password}
            confirmPassword={confirmPassword}
            onSubmit={onSubmit}
          />
        </div>
      </Card>
    </Flex>
  );
}

/**
 * The props for the PasswordResetPage component.
 *
 * @param variant The variant of the password reset page.
 */
type PasswordResetPageWrapperProps = {
  variant: PasswordResetPageVariant;
};

export function PasswordResetPageWrapper(props: PasswordResetPageWrapperProps): JSX.Element {
  const { variant } = props;
  const isRegisterVariant = variant === PasswordResetPageVariant.REGISTER;

  // Get authentication tokens
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const registrationToken = searchParams.get('registration_token') || '';

  const { isError: registrationShouldRedirect, isSuccess: registrationShouldRender } = useQuery({
    queryKey: [CandidateExperienceQueryKeys.REGISTRATION_STATUS],
    queryFn: () => {
      return getRegistrationStatus(registrationToken);
    },
    enabled: isRegisterVariant,
  });

  // TODO: For the AUTH variant, get authentication tokens from Cookies
  const authShouldRedirect = false;
  const authShouldRender = false;

  const shouldRedirect = isRegisterVariant ? registrationShouldRedirect : authShouldRedirect;
  const shouldRender = isRegisterVariant ? registrationShouldRender : authShouldRender;
  if (shouldRedirect) {
    return <Redirect to="/login" />;
  } else if (shouldRender) {
    return <PasswordResetPage variant={variant} registrationToken={registrationToken} />;
  } else {
    return <div>Loading...</div>;
  }
}
